import api from '@/helpers/api'

const state = {
    pointControl: null, 
    newAuthModule: null,
    marketingModule: null,
    transactionReportModule: null,
    thresholdSetting: null,
    newsletterModule: null,
    benefitModule: null,
    promotionsModule: null,
    branchProductManagement: null,
    merchantProduct: null,
    merchantAccessToken: null,
    voucherGift: null,
    bookingModule: null,
    allFlags: [], 
    loading: false,
    error: null
}

const getters = {
    getPointControlFlag: (state) => state.pointControl?.flag?.toUpperCase() || 'DECENTRALIZE',
    getNewAuthModuleFlag: (state) => state.newAuthModule?.flag?.toUpperCase() || 'NONACTIVE',
    getMarketingModuleFlag: (state) => state.marketingModule?.flag?.toUpperCase() || 'ACTIVE',
    getTransactionReportModuleFlag: (state) => state.transactionReportModule?.flag?.toUpperCase() || 'ACTIVE',
    getThresholdSettingFlag: (state) => state.thresholdSetting?.flag?.toUpperCase() || 'ACTIVE',
    getNewsletterModuleFlag: (state) => state.newsletterModule?.flag?.toUpperCase() || 'ACTIVE',
    getBenefitModuleFlag: (state) => state.benefitModule?.flag?.toUpperCase() || 'ACTIVE',
    getPromotionsModuleFlag: (state) => state.promotionsModule?.flag?.toUpperCase() || 'ACTIVE',
    getBranchProductManagementFlag: (state) => state.branchProductManagement?.flag?.toUpperCase() || 'ACTIVE',
    getMerchantProductFlag: (state) => state.merchantProduct?.flag?.toUpperCase() || 'ACTIVE',
    getMerchantAccessTokenFlag: (state) => state.merchantAccessToken?.flag?.toUpperCase() || 'NONACTIVE',
    getVoucherGiftFlag: (state) => state.voucherGift?.flag?.toUpperCase() || 'ACTIVE',
    getBookingModuleFlag: (state) => state.bookingModule?.flag?.toUpperCase() || 'ACTIVE',
    getMetadata: (state) => state.pointControl?.metadata || {},
    isLoading: (state) => state.loading,
    hasError: (state) => state.error
}

const mutations = {
    SET_POINT_CONTROL(state, payload) {
        state.pointControl = payload
    },

    SET_NEW_AUTH_MODULE(state, payload) {
        state.newAuthModule = payload
    },

    SET_MARKETING_MODULE(state, payload) {
        state.marketingModule = payload
    },

    SET_TRANSACTION_REPORT_MODULE(state, payload) {
        state.transactionReportModule = payload
    },

    SET_THRESHOLD_SETTING(state, payload) {
        state.thresholdSetting = payload
    },

    SET_NEWSLETTER_MODULE(state, payload) {
        state.newsletterModule = payload
    },

    SET_BENEFIT_MODULE(state, payload) {
        state.benefitModule = payload
    },

    SET_PROMOTIONS_MODULE(state, payload) {
        state.promotionsModule = payload
    },

    SET_BRANCH_PRODUCT_MANAGEMENT(state, payload) {
        state.branchProductManagement = payload
    },

    SET_MERCHANT_PRODUCT(state, payload) {
        state.merchantProduct = payload
    },

    SET_MERCHANT_ACCESS_TOKEN(state, payload) {
        state.merchantAccessToken = payload
    },

    SET_VOUCHER_GIFT(state, payload) {
        state.voucherGift = payload
    },

    SET_BOOKING_MODULE(state, payload) {
        state.bookingModule = payload
    },

    SET_ALL_FLAGS(state, payload) {
        state.allFlags = payload
    },

    SET_LOADING(state, payload) {
        state.loading = payload
    },

    SET_ERROR(state, payload) {
        state.error = payload
    },

    CLEAR_ERROR(state) {
        state.error = null
    }
}

const actions = {
    /**
     * Fetch all feature flags (for root user)
     */
    async fetchAllFlags({ commit }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const response = await api.request('get', '/api/feature-flags', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            })
            const flags = response.data.data

            commit('SET_ALL_FLAGS', flags)
            return flags
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch feature flags'
            commit('SET_ERROR', errorMessage)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    /**
     * Fetch feature flag for corporate
     * @param {Object} context 
     * @param {String} corporateId 
     */
    async fetchFeatureFlag({ commit }, corporateId) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const response = await api.request('get', `/api/feature-flags/${corporateId}`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            })
            // Backend now returns an object with features as keys
            const flags = response.data.data || {}

            commit('SET_POINT_CONTROL', flags.point_control || { flag: 'DECENTRALIZE', metadata: {} })
            commit('SET_NEW_AUTH_MODULE', flags.new_auth_module || { flag: 'NONACTIVE', metadata: {} })
            commit('SET_MARKETING_MODULE', flags.marketing_module || { flag: 'ACTIVE', metadata: {} })
            commit('SET_TRANSACTION_REPORT_MODULE', flags.transaction_report_module || { flag: 'ACTIVE', metadata: {} })
            commit('SET_THRESHOLD_SETTING', flags.threshold_setting || { flag: 'ACTIVE', metadata: {} })
            commit('SET_NEWSLETTER_MODULE', flags.newsletter_module || { flag: 'ACTIVE', metadata: {} })
            commit('SET_BENEFIT_MODULE', flags.benefit_module || { flag: 'ACTIVE', metadata: {} })
            commit('SET_PROMOTIONS_MODULE', flags.promotions_module || { flag: 'ACTIVE', metadata: {} })
            commit('SET_BRANCH_PRODUCT_MANAGEMENT', flags.branch_product_management || { flag: 'ACTIVE', metadata: {} })
            commit('SET_MERCHANT_PRODUCT', flags.merchant_product || { flag: 'ACTIVE', metadata: {} })
            commit('SET_MERCHANT_ACCESS_TOKEN', flags.merchant_access_token || { flag: 'NONACTIVE', metadata: {} })
            commit('SET_VOUCHER_GIFT', flags.voucher_gift || { flag: 'ACTIVE', metadata: {} })
            commit('SET_BOOKING_MODULE', flags.booking_module || { flag: 'ACTIVE', metadata: {} })
            return flags
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch feature flag'
            commit('SET_ERROR', errorMessage)

            if (error.response?.status === 404) {
                commit('SET_POINT_CONTROL', {
                    corporate_id: corporateId,
                    flag: 'DECENTRALIZE',
                    metadata: {}
                })
                commit('SET_NEW_AUTH_MODULE', {
                    corporate_id: corporateId,
                    flag: 'NONACTIVE',
                    metadata: {}
                })
                commit('SET_MARKETING_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_TRANSACTION_REPORT_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_THRESHOLD_SETTING', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_NEWSLETTER_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_BENEFIT_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_PROMOTIONS_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_BRANCH_PRODUCT_MANAGEMENT', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_MERCHANT_PRODUCT', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_MERCHANT_ACCESS_TOKEN', {
                    corporate_id: corporateId,
                    flag: 'NONACTIVE',
                    metadata: {}
                })
                commit('SET_VOUCHER_GIFT', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
                commit('SET_BOOKING_MODULE', {
                    corporate_id: corporateId,
                    flag: 'ACTIVE',
                    metadata: {}
                })
            }

            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    /**
     * Update feature flag
     * @param {Object} context 
     * @param {Object} payload - { corporateId, feature, flag, metadata }
     */
    async updateFeatureFlag({ commit }, { corporateId, feature, flag, metadata = {} }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const response = await api.request('put', `/api/feature-flags/${corporateId}`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            }, {
                feature,
                flag,
                metadata
            })

            const updatedFlag = response.data.data
            
            if (feature === 'point_control') {
                commit('SET_POINT_CONTROL', updatedFlag)
            } else if (feature === 'new_auth_module') {
                commit('SET_NEW_AUTH_MODULE', updatedFlag)
            } else if (feature === 'marketing_module') {
                commit('SET_MARKETING_MODULE', updatedFlag)
            } else if (feature === 'transaction_report_module') {
                commit('SET_TRANSACTION_REPORT_MODULE', updatedFlag)
            } else if (feature === 'threshold_setting') {
                commit('SET_THRESHOLD_SETTING', updatedFlag)
            } else if (feature === 'newsletter_module') {
                commit('SET_NEWSLETTER_MODULE', updatedFlag)
            } else if (feature === 'promotions_module') {
                commit('SET_PROMOTIONS_MODULE', updatedFlag)
            } else if (feature === 'branch_product_management') {
                commit('SET_BRANCH_PRODUCT_MANAGEMENT', updatedFlag)
            } else if (feature === 'merchant_product') {
                commit('SET_MERCHANT_PRODUCT', updatedFlag)
            } else if (feature === 'merchant_access_token') {
                commit('SET_MERCHANT_ACCESS_TOKEN', updatedFlag)
            } else if (feature === 'voucher_gift') {
                commit('SET_VOUCHER_GIFT', updatedFlag)
            } else if (feature === 'booking_module') {
                commit('SET_BOOKING_MODULE', updatedFlag)
            }

            return updatedFlag
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to update feature flag'
            commit('SET_ERROR', errorMessage)
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    /**
     * Fetch audit logs
     * @param {Object} context 
     * @param {Object} payload - { corporateId, limit }
     */
    async fetchAuditLogs({ commit }, { corporateId, limit = 50 }) {
        try {
            const response = await api.request('get', `/api/feature-flags/${corporateId}/audit-logs?limit=${limit}`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            })

            return response.data.data
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to fetch audit logs'
            commit('SET_ERROR', errorMessage)
            throw error
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
